import { getCurrentTimezone } from "@/lib/timezone";
import { useShallow } from "zustand/shallow";
import { CandidateInterview, CandidateJobOpening } from "@/types";
import { create } from "zustand";

export interface TimeRange {
  id?: string;
  startTime: string; // e.g. "13:00"
  endTime: string;
  timezone: string; // e.g. "America/Chicago"
  placeholder?: boolean;
}

export type DayAvailabilities = TimeRange[];

export interface SpecificAvailabilities {
  dates: string[]; // YYYY-MM-DD
  times: TimeRange[];
}

export const newWeek = Array.from({ length: 7 }, () => []).map((_, i) =>
  i > 0 && i < 6
    ? [
        {
          startTime: "",
          endTime: "",
          timezone: getCurrentTimezone(),
          placeholder: true,
        },
      ]
    : [],
);

interface AvailabilitySheet {
  opportunity: CandidateJobOpening | null;
  setOpportunity: (opportunity: CandidateJobOpening) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  successOpen: boolean;
  setSuccessOpen: (successOpen: boolean) => void;
  timezone: string;
  setTimezone: (timezone: string) => void;
  interview: CandidateInterview | null;
  setInterview: (interview: CandidateInterview) => void;
  // form state
  dayAvailabilities: DayAvailabilities[];
  setDayAvailabilities: (dayAvailabilities: DayAvailabilities[]) => void;
  specificAvailabilities: SpecificAvailabilities[];
  setSpecificAvailabilities: (
    specificAvailabilities: SpecificAvailabilities[],
  ) => void;
  // specific availability popover
  specificOpen: boolean;
  setSpecificOpen: (specificOpen: boolean) => void;
  specificAvailabilitiesForm: SpecificAvailabilities;
  setSpecificAvailabilitiesForm: (
    callback: (
      data: AvailabilitySheet["specificAvailabilitiesForm"],
    ) => AvailabilitySheet["specificAvailabilitiesForm"],
  ) => void;
  clearSpecificAvailabilitiesForm: () => void;
  confirmSpecificAvailabilities: () => void;
}

export const useAvailabilitySheet = create<AvailabilitySheet>((set, get) => ({
  opportunity: null,
  setOpportunity: (opportunity) => {
    const open = opportunity === null ? false : true;
    set({ opportunity, open });
  },
  open: false,
  setOpen: (open) => {
    if (open) {
      set({ open });
      return;
    }

    set({ open });
    // timeout to not have layout shift when closing
    setTimeout(() => {
      get().clearSpecificAvailabilitiesForm();
      set({
        open,
        opportunity: null,
        specificOpen: false,
        specificAvailabilities: [],
        dayAvailabilities: [],
        timezone: getCurrentTimezone(),
      });
    }, 100);
  },
  successOpen: false,
  setSuccessOpen: (successOpen) => set({ successOpen }),
  timezone: getCurrentTimezone(),
  setTimezone: (timezone) => {
    const dayAvailabilities = get().dayAvailabilities.map((day) =>
      day.map((t) => ({ ...t, timezone })),
    );
    const specificAvailabilities = get().specificAvailabilities.map((day) => ({
      ...day,
      times: day.times.map((t) => ({ ...t, timezone })),
    }));

    set({
      timezone: timezone,
      dayAvailabilities,
      specificAvailabilities,
    });
  },
  interview: null,
  setInterview: (interview) => set({ interview }),
  dayAvailabilities: newWeek,
  setDayAvailabilities: (dayAvailabilities) => {
    set({ dayAvailabilities });
  },
  specificAvailabilities: [],
  setSpecificAvailabilities: (specificAvailabilities) => {
    specificAvailabilities.sort((a, b) => {
      const [aDate] = a.dates;
      const [bDate] = b.dates;

      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;

      return aDate.localeCompare(bDate);
    });
    set({ specificAvailabilities });
  },
  specificOpen: false,
  setSpecificOpen: (specificOpen) => set({ specificOpen }),
  specificAvailabilitiesForm: {
    dates: [],
    times: [
      {
        startTime: "",
        endTime: "",
        timezone: getCurrentTimezone(),
        placeholder: true,
      },
    ],
  },
  setSpecificAvailabilitiesForm: (callback) => {
    const data = callback(get().specificAvailabilitiesForm);
    data.times = data.times.sort((a, b) =>
      a.startTime.localeCompare(b.startTime),
    );
    set({
      specificAvailabilitiesForm: data,
    });
  },
  clearSpecificAvailabilitiesForm: () =>
    set({
      specificAvailabilitiesForm: {
        dates: [],
        times: [
          {
            startTime: "",
            endTime: "",
            timezone: getCurrentTimezone(),
            placeholder: true,
          },
        ],
      },
    }),
  confirmSpecificAvailabilities: () => {
    const { dates, times } = get().specificAvailabilitiesForm;
    if (
      !dates.length ||
      !times.length ||
      times.find((time) => !time.startTime || !time.endTime)
    ) {
      return;
    }

    const specificAvailabilities = [...get().specificAvailabilities];
    specificAvailabilities.push({
      dates,
      times,
    });
    set({ specificAvailabilities, specificOpen: false });
    get().clearSpecificAvailabilitiesForm();
  },
}));

export const useAvailabilitySheetState = () =>
  useAvailabilitySheet(
    useShallow(
      (s) =>
        [
          s.open,
          s.setOpen,
          s.opportunity,
          s.interview,
          s.successOpen,
          s.setSuccessOpen,
          s.setDayAvailabilities,
          s.setSpecificAvailabilities,
        ] as const,
    ),
  );
